import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, InlineAlert, cn } from "../../shared/ui";
import type { PermissionView, RoleView } from "../../shared/types/api";

/**
 * Edits the permissions a role grants (§12).
 *
 * <p>A whole-set replacement, matching the endpoint. A role's power should be decided as one
 * deliberate statement; two interleaved partial edits from different administrators could leave a
 * role holding a combination neither of them chose.
 *
 * <p>Permissions are grouped by category and each category can be selected wholesale, because the
 * realistic edit is "this role should be able to do timesheets" rather than four separate ticks.
 */
export function RolePermissionsDrawer({
  role,
  permissions,
  onClose,
  onSaved,
}: {
  role: RoleView | null;
  permissions: PermissionView[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setSelected(new Set(role.permissions));
      setError(null);
    }
  }, [role]);

  const byCategory = useMemo(
    () =>
      permissions.reduce<Record<string, PermissionView[]>>((accumulator, permission) => {
        (accumulator[permission.category] ??= []).push(permission);
        return accumulator;
      }, {}),
    [permissions],
  );

  const toggle = (code: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });

  const toggleCategory = (items: PermissionView[]) => {
    const allSelected = items.every((permission) => selected.has(permission.code));
    setSelected((current) => {
      const next = new Set(current);
      items.forEach((permission) => {
        if (allSelected) {
          next.delete(permission.code);
        } else {
          next.add(permission.code);
        }
      });
      return next;
    });
  };

  const submit = async () => {
    if (!role) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/roles/${role.id}/permissions`, {
        permissions: [...selected],
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the permissions.");
    } finally {
      setSaving(false);
    }
  };

  const changed =
    role !== null &&
    (selected.size !== role.permissions.length ||
      role.permissions.some((code) => !selected.has(code)));

  return (
    <Drawer
      open={Boolean(role)}
      onClose={onClose}
      title={role?.name ?? "Role"}
      subtitle={`${selected.size} of ${permissions.length} permissions`}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={!changed}>
            {saving ? "Saving…" : "Save permissions"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          Changes take effect the next time each holder refreshes their session, within about
          fifteen minutes. Permissions for later phases can be granted now; they simply have no
          endpoints to reach yet.
        </InlineAlert>

        {role?.code === "SYSTEM_ADMIN" && (
          <InlineAlert tone="warning">
            Removing permissions from SYSTEM_ADMIN can lock everyone out of administration. Make
            sure another role still holds ROLE_MANAGE and USER_MANAGE.
          </InlineAlert>
        )}

        {Object.entries(byCategory).map(([category, items]) => {
          const allSelected = items.every((permission) => selected.has(permission.code));
          const someSelected = items.some((permission) => selected.has(permission.code));

          return (
            <section key={category}>
              <div className="mb-1.5 flex items-center justify-between">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  {category}
                </h3>
                <button
                  type="button"
                  onClick={() => toggleCategory(items)}
                  className="text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
                >
                  {allSelected ? "Clear all" : "Select all"}
                </button>
              </div>

              <div
                className={cn(
                  "overflow-hidden rounded-lg border",
                  someSelected ? "border-brand-200" : "border-ink-200",
                )}
              >
                {items.map((permission) => (
                  <label
                    key={permission.code}
                    className="flex cursor-pointer items-start gap-3 border-b border-ink-100 px-3 py-2.5 transition-colors last:border-0 hover:bg-ink-50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(permission.code)}
                      onChange={() => toggle(permission.code)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
                    />
                    <span className="min-w-0">
                      <span className="block font-mono text-xs text-ink-700">
                        {permission.code}
                      </span>
                      {permission.description && (
                        <span className="block text-sm text-ink-500">
                          {permission.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </Drawer>
  );
}
